import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
} from '@pflegenavi/shared-frontend/platform';
import { Filter } from '../filter';
import type {
  TransactionType,
  TransactionSourceType,
  TransactionInitiatedBy,
  SourceOfMandate,
} from '@pflegenavi/shared/api';

export interface GetTransactionsPhoenixParams {
  page?: number;
  page_size?: number;
  nursing_home_id?: string;
  resident_id?: string;
  family_member_id?: string | null;
  type?: TransactionType[];
  source_type?: TransactionSourceType[];
  initiated_by?: TransactionInitiatedBy[];
  creation_date_range?: [Date | undefined, Date | undefined];
  submission_date_range?: [Date | undefined, Date | undefined];
  receipt_date_range?: [Date | undefined, Date | undefined];
  service_provider_ids?: string[];
  receipt_type_ids?: string[];
  creator_ids?: string[];
  search?: string;
  order_by?: string[];
  order_directions?: Array<'asc' | 'desc'>;
}

export type ReceiptTypeName =
  | 'Friseur'
  | 'Apotheke'
  | 'Pediküre'
  | 'Maniküre'
  | 'Supermarkt'
  | 'Andere'
  | 'Barauslage';

export interface ReceiptTypePhoenix {
  id: string;
  name: ReceiptTypeName;
}

export interface ServiceProviderPhoenix {
  id: string;
  name: string;
}

export interface TransactionPhoenixReceipt {
  id: string;
  receipt_date: string;
  receipt_not_available: boolean;
  created_on: string;
}

export interface TransactionPhoenixRecurringItem {
  id: string;
  name: string;
}

export interface TransactionPhoenixResident {
  id: string;
  firstname: string;
  lastname: string;
}

export interface TransactionPhoenixDto {
  id: string;
  type: TransactionType;
  source_type: TransactionSourceType;
  initiated_by: TransactionInitiatedBy;
  amount: number;
  additional_fee_amount: number;
  included_fee_amount: number;
  additional_fees_diverted_to_pflegenavi: boolean;
  creation_date: string; // The creation date field
  submission_date: string | null;
  receipt_date: string | null;
  created_on: string; // This is inserted_at (ISO date string)
  creator_id: string;
  creator_name: string;
  notes: string | null;
  cancelled: boolean;
  disputed: boolean;
  cancelled_submission_date: string | null;
  sequence_id: string;
  resident: TransactionPhoenixResident;
  family_member_id: string | null;
  receipt: TransactionPhoenixReceipt | null;
  receipt_image_ids: string[];
  recurring_item: TransactionPhoenixRecurringItem | null;
  receipt_type: ReceiptTypePhoenix | null;
  service_provider: ServiceProviderPhoenix | null;
  creator?: {
    first_name: string;
    last_name: string;
  };
  status?: string;
  receipt_state?: string;
  source_of_mandate?: SourceOfMandate | null;
  initiation_date: string | null;
}

export interface GetTransactionsPhoenixResult {
  data: TransactionPhoenixDto[];
  meta: {
    current_page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next_page: boolean;
    has_previous_page: boolean;
    next_page: number | null;
    previous_page: number | null;
  };
}

export interface GetFamilyMemberTransactionsPhoenixParams {
  tenant: string;
  resident_id: string;
  family_member_id?: string | null;
  source_types?: TransactionSourceType[];
}

export interface FamilyMemberTransactionPhoenixResident {
  id: string;
  first_name: string;
  last_name: string;
}

export interface FamilyMemberTransactionPhoenixDto {
  id: string;
  type: TransactionType;
  source_type: TransactionSourceType;
  initiated_by: TransactionInitiatedBy;
  resident: FamilyMemberTransactionPhoenixResident;
  family_member_id: string | null;
  receipt_id: string | null;
  receipt_type: ReceiptTypeName | null;
  receipt_date: string | null;
  receipt_created_on: string | null;
  receipt_image_ids: string[];
  receipt_not_available: boolean;
  service_provider: string | null;
  amount: number;
  additional_fee_amount: number;
  included_fee_amount: number;
  additional_fees_diverted_to_pflegenavi: boolean;
  date: string;
  created_on: string;
  notes: string | null;
  creator_first_name: string | null;
  creator_last_name: string | null;
  cancelled: boolean;
  disputed: boolean;
  cancelled_submission_date: string | null;
  recurring_item: TransactionPhoenixRecurringItem | null;
  iban_last4: string | null;
  source_of_mandate: SourceOfMandate | null;
  sequence_id: string;
  initiation_date: string | null;
}

export interface GetFamilyMemberTransactionsPhoenixResult {
  data: FamilyMemberTransactionPhoenixDto[];
}

export interface ITransactionPhoenixApi extends Api {
  getTransactions(dataIn: {
    params: GetTransactionsPhoenixParams;
  }): Promise<GetTransactionsPhoenixResult>;
  getTransactionsForFamilyMember(dataIn: {
    params: GetFamilyMemberTransactionsPhoenixParams;
  }): Promise<GetFamilyMemberTransactionsPhoenixResult>;
}

export class TransactionPhoenixApi implements ITransactionPhoenixApi {
  baseUrl: string;
  familyMemberBaseUrl: string;

  getTransactions: ITransactionPhoenixApi['getTransactions'];
  getTransactionsForFamilyMember: ITransactionPhoenixApi['getTransactionsForFamilyMember'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);
    this.familyMemberBaseUrl = getPhoenixApiTenantUrl('family-member', apiUrl);

    this.getTransactionsForFamilyMember = get({
      authContext,
      url: (params) => {
        const filter = new Filter();

        if (params.family_member_id !== undefined) {
          filter.field(
            'family_member_id',
            params.family_member_id === null ? 'empty' : '==',
            params.family_member_id === null ? 'true' : params.family_member_id
          );
        }

        filter.fieldIfPresent('source_type', 'in', params.source_types);

        const query = filter.build();
        const search = query.toString();
        return `${this.familyMemberBaseUrl}/${params.tenant}/${
          params.resident_id
        }/transactions${search ? `?${search}` : ''}`;
      },
    });

    this.getTransactions = get({
      authContext,
      url: (params) => {
        const filter = new Filter();

        filter.fieldIfPresent('nursing_home_id', '==', params.nursing_home_id);
        filter.fieldIfPresent('resident_id', '==', params.resident_id);

        // Family member filter
        if (params.family_member_id !== undefined) {
          filter.field(
            'family_member_id',
            params.family_member_id === null ? 'empty' : '==',
            params.family_member_id === null
              ? undefined
              : params.family_member_id
          );
        }

        filter.fieldIfPresent('type', 'in', params.type);
        filter.fieldIfPresent('source_type', 'in', params.source_type);
        filter.fieldIfPresent('initiated_by', 'in', params.initiated_by);

        // Date range filters
        filter.fieldIfPresent(
          'creation_date',
          '>=',
          params.creation_date_range?.[0]?.toISOString().split('T')[0]
        );
        filter.fieldIfPresent(
          'creation_date',
          '<=',
          params.creation_date_range?.[1]?.toISOString().split('T')[0]
        );

        filter.fieldIfPresent(
          'submission_date',
          '>=',
          params.submission_date_range?.[0]?.toISOString()
        );
        filter.fieldIfPresent(
          'submission_date',
          '<=',
          params.submission_date_range?.[1]?.toISOString()
        );

        filter.fieldIfPresent(
          'receipt_date',
          '>=',
          params.receipt_date_range?.[0]?.toISOString().split('T')[0]
        );
        filter.fieldIfPresent(
          'receipt_date',
          '<=',
          params.receipt_date_range?.[1]?.toISOString().split('T')[0]
        );

        filter.fieldIfPresent(
          'service_provider_id',
          'in',
          params.service_provider_ids
        );
        filter.fieldIfPresent('receipt_type_id', 'in', params.receipt_type_ids);
        filter.fieldIfPresent('creator_id', 'in', params.creator_ids);

        const query = filter.build();

        // Pagination params
        if (params.page !== undefined) {
          query.append('page', params.page.toString());
        }
        if (params.page_size !== undefined) {
          query.append('page_size', params.page_size.toString());
        }

        // Sorting params
        if (params.order_by) {
          params.order_by.forEach((field) => {
            query.append('order_by[]', field);
          });
        }
        if (params.order_directions) {
          params.order_directions.forEach((direction) => {
            query.append('order_directions[]', direction);
          });
        }

        // Search param (compound: notes, service_provider_name, receipt_type_name, amount)
        if (params.search) {
          query.append('search', params.search);
        }

        return `${this.baseUrl}/transactions?${query.toString()}`;
      },
    });
  }
}
